import useRecipeutils from "../utils/recipeUtils";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { useShoppingList } from "../context/ShoppingListProvider";
import {
  INGREDIENT_SLOTS,
  formatQty,
  getRecipeIngredients,
  formatIngredientLine,
  recipeMatchesSearch,
  recipeToClipboardText,
  recipeToCreatePayload,
  getRecipeLineage,
} from "../utils/recipeIngredients";
import { Link } from "react-router-dom";
import '../App.css';
import { useState, useEffect, useMemo } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Collapsible from 'react-collapsible';
import { Modal, Button, Nav } from 'react-bootstrap';

const MEASUREMENT_OPTIONS = [
  { value: "Cups", label: "Cups" },
  { value: "Cup", label: "Cup" },
  { value: "fluid oz", label: "fluid oz" },
  { value: "dry oz", label: "dry oz" },
  { value: "lb", label: "Pounds" },
  { value: "Quarts", label: "Quarts" },
  { value: "Quart", label: "Quart" },
  { value: "Grams", label: "Grams" },
  { value: "teaspoon", label: "teaspoon" },
  { value: "Tablespoon", label: "Tablespoon" },
  { value: "Whole", label: "Whole" },
];

const SCALE_PRESETS = [0.5, 1, 2, 3];
const BOOK_META = {
  community: {
    title: 'Community Book',
    icon: 'bi-people',
    description: 'Browse recipes shared by the Recipe Lab community.',
    endpoint: '/recipies/public',
    empty: 'No community recipes yet. Be the first to publish one from My Recipes!',
  },
  mine: {
    title: 'My Recipes',
    icon: 'bi-journal-bookmark',
    description: 'Your personal collection. Keep recipes private or publish them for everyone.',
    endpoint: '/recipies/mine',
    empty: 'You have not saved any recipes yet. Tap "New Recipe" to create your first one.',
  },
  favorites: {
    title: 'Favorites',
    icon: 'bi-heart',
    description: 'Quick access to recipes you have saved from the community or your own book.',
    endpoint: '/recipies/favorites',
    empty: 'No favorites yet. Tap the heart on any recipe to save it here.',
  },
};

function IngredientRow({ quantity, measurement, ingredientName, isCompleted, onToggleDone, onAddToList }) {
  if (!ingredientName && !Number(quantity)) return null;

  return (
    <div className={`ingredient-row ${isCompleted ? 'completed' : ''}`}>
      <div className="ingredient-info">
        <div className="qty">{[quantity, measurement].filter(Boolean).join(' ')}</div>
        <div className="ingredient-name">{ingredientName}</div>
      </div>
      <div className="ingredient-row-actions">
        {onAddToList && (
          <button
            className="ingredient-add-btn"
            onClick={onAddToList}
            type="button"
            title="Add to shopping list"
          >
            <i className="bi bi-basket" />
          </button>
        )}
        <button
          className={`done-btn ${isCompleted ? 'undo' : ''}`}
          onClick={onToggleDone}
          type="button"
        >
          {isCompleted ? 'Undo' : 'Done'}
        </button>
      </div>
    </div>
  );
}

function RecipeManagePanel({ recipe, onSave, onDelete }) {
  const [name, setName] = useState(recipe.recipiename || '');
  const [notes, setNotes] = useState(recipe.notes || '');
  const [draft, setDraft] = useState(() => recipeToCreatePayload(recipe));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateSlot = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Recipe name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...draft,
        recipiename: name.trim(),
        notes,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not save recipe.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="recipe-manage-form">
      <label className="recipe-field-label">Recipe name</label>
      <input
        className="informationinput recipe-field"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <label className="recipe-field-label">Ingredients</label>
      {INGREDIENT_SLOTS.map((n) => (
        <div className="recipe-form-ingredient" key={n}>
          <span className="recipe-form-ingredient-num">{n}</span>
          <input
            className="numinput"
            type="number"
            min="0"
            step="any"
            value={draft[`ingediant${n}num`] || ''}
            onChange={(e) => updateSlot(`ingediant${n}num`, e.target.value === '' ? 0 : e.target.value)}
          />
          <select
            value={draft[`measurement${n}`] || 'Cups'}
            onChange={(e) => updateSlot(`measurement${n}`, e.target.value)}
          >
            {MEASUREMENT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <input
            className="informationinput"
            type="text"
            placeholder="Ingredient"
            value={draft[`ingediant${n}`] || ''}
            onChange={(e) => updateSlot(`ingediant${n}`, e.target.value)}
          />
        </div>
      ))}

      <label className="recipe-field-label">Notes</label>
      <textarea
        className="notes recipe-field"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      {error && <p className="recipe-manage-error">{error}</p>}

      <button type="button" className="recipe-btn recipe-btn-primary" onClick={handleSave} disabled={saving}>
        <i className="bi bi-save" /> {saving ? 'Saving...' : 'Save changes'}
      </button>
      <button
        type="button"
        className="recipe-btn recipe-btn-danger"
        onClick={() => onDelete({ id: recipe.id, name: recipe.recipiename })}
      >
        <i className="bi bi-trash" /> Delete recipe
      </button>
    </div>
  );
}

function Recipes() {
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { addItems } = useShoppingList();

  const {
    recipieslist,
    searchTerm, setSearchTerm,
    getRecipies, setRecipiename, setMeasurement1,
    setMeasurement2, setMeasurement3, setMeasurement4,
    setMeasurement5, setMeasurement6, setMeasurement7, setMeasurement8,
    setMeasurement9, setMeasurement10, setMeasurement11, setMeasurement12,
    setIngediant1, setIngediant2, setIngediant3, setIngediant4,
    setIngediant5, setIngediant6, setIngediant7, setIngediant8,
    setIngediant9, setIngediant10, setIngediant11, setIngediant12,
    setIngediant1num, setIngediant2num, setIngediant3num, setIngediant4num,
    setIngediant5num, setIngediant6num, setIngediant7num, setIngediant8num,
    setIngediant9num, setIngediant10num, setIngediant11num, setIngediant12num,
    setNotes,
    addRecipie,
    copyRecipe,
    updateRecipe,
    resetRecipeForm,
    loadFavoriteIds, isFavorite, toggleFavorite, publishRecipe,
    deleteRecipe, addImglink, addRecipieprops,
    buildname, setBuildname, buildId, setBuildId,
    buildDescription, setBuildDescription,
    setBuildimagepath, setBuildimagepath2,
    show, handleClose, handleShow
  } = useRecipeutils();

  const setNumFns = [null,
    setIngediant1num, setIngediant2num, setIngediant3num, setIngediant4num,
    setIngediant5num, setIngediant6num, setIngediant7num, setIngediant8num,
    setIngediant9num, setIngediant10num, setIngediant11num, setIngediant12num
  ];
  const setMeasFns = [null,
    setMeasurement1, setMeasurement2, setMeasurement3, setMeasurement4,
    setMeasurement5, setMeasurement6, setMeasurement7, setMeasurement8,
    setMeasurement9, setMeasurement10, setMeasurement11, setMeasurement12
  ];
  const setIngFns = [null,
    setIngediant1, setIngediant2, setIngediant3, setIngediant4,
    setIngediant5, setIngediant6, setIngediant7, setIngediant8,
    setIngediant9, setIngediant10, setIngediant11, setIngediant12
  ];

  const [activeBook, setActiveBook] = useState('community');
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [publishTarget, setPublishTarget] = useState(null);
  const [completedIngredients, setCompletedIngredients] = useState({});
  const [multipliers, setMultipliers] = useState({});
  const [fileData, setFileData] = useState(null);
  const [buildPreviewUrl, setBuildPreviewUrl] = useState('');
  const [buildSaving, setBuildSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [originalPreview, setOriginalPreview] = useState(null);

  useEffect(() => {
    loadFavoriteIds();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRecipes(activeBook);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!statusMessage) return undefined;
    const timer = setTimeout(() => setStatusMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [statusMessage]);

  useEffect(() => {
    if (!highlightId || isLoading) return undefined;
    const card = document.getElementById(`recipe-card-${highlightId}`);
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setOriginalPreview(null);
      const timer = setTimeout(() => setHighlightId(null), 4500);
      return () => clearTimeout(timer);
    }
    if (
      searchTerm
      && recipieslist.some((recipe) => Number(recipe.id) === Number(highlightId))
    ) {
      setSearchTerm('');
      return undefined;
    }
    if (activeBook !== 'community') return undefined;

    let cancelled = false;
    axiosPrivate.get(`/recipies/recipe/${highlightId}`)
      .then((res) => {
        if (cancelled) return;
        const found = Array.isArray(res.data) ? res.data[0] : res.data;
        setOriginalPreview((prev) => ({
          ...(prev || {}),
          pending: false,
          recipe: found,
          missing: !found,
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setOriginalPreview((prev) => ({
          ...(prev || {}),
          pending: false,
          missing: true,
        }));
      });
    return () => { cancelled = true; };
  }, [highlightId, isLoading, activeBook, recipieslist, axiosPrivate, searchTerm, setSearchTerm]);

  const showStatus = (message) => setStatusMessage(message);

  const loadRecipes = async (book = activeBook) => {
    setIsLoading(true);
    try {
      await getRecipies(BOOK_META[book].endpoint);
    } catch (err) {
      if (err.isRateLimited) {
        showStatus(err.message);
      } else {
        showStatus('Could not load recipes. Please try again.');
        console.error(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const switchBook = (book) => {
    if (isLoading || book === activeBook) return;
    setActiveBook(book);
    setShowAddRecipe(false);
    loadRecipes(book);
  };

  const filteredRecipes = useMemo(() => {
    return recipieslist.filter((recipe) => recipeMatchesSearch(recipe, searchTerm));
  }, [recipieslist, searchTerm]);

  const handlePublishConfirm = async () => {
    if (!publishTarget) return;
    try {
      await publishRecipe(publishTarget.id);
      setPublishTarget(null);
      showStatus(`"${publishTarget.name}" is now in the Community Book!`);
    } catch (err) {
      const msg = err.response?.data?.message
        || 'Could not publish this recipe.';
      showStatus(msg);
      if (err.response?.status === 402) setPublishTarget(null);
    }
  };

  const toggleDone = (recipeId, ingredientNum) => {
    const key = `${recipeId}-${ingredientNum}`;
    setCompletedIngredients((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getMultiplier = (recipeId) => multipliers[recipeId] || 1;

  const setRecipeScale = (recipeId, value) => {
    setMultipliers((prev) => ({ ...prev, [recipeId]: value }));
  };

  const isRecipeOwner = (recipe) =>
    auth?.user && recipe.owner_username === auth.user;

  const canManage = (recipe) =>
    activeBook === 'mine' && isRecipeOwner(recipe);

  const canAddBuild = (recipe) =>
    activeBook === 'mine' && isRecipeOwner(recipe);

  const clearBuildPreview = () => {
    if (buildPreviewUrl) {
      URL.revokeObjectURL(buildPreviewUrl);
    }
    setBuildPreviewUrl('');
  };

  const fileChangeHandler = (e) => {
    const file = e.target.files?.[0] || null;
    clearBuildPreview();
    setFileData(file);
    if (file) {
      setBuildimagepath(file.name);
      setBuildPreviewUrl(URL.createObjectURL(file));
    } else {
      setBuildimagepath('');
    }
  };

  const closeBuildModal = () => {
    clearBuildPreview();
    setFileData(null);
    setBuildimagepath('');
    handleClose();
  };

  const openBuildModal = (recipe) => {
    clearBuildPreview();
    setBuildId(recipe.id);
    setBuildname(recipe.recipiename);
    setBuildDescription('');
    setBuildimagepath('');
    setBuildimagepath2('');
    setFileData(null);
    handleShow();
  };

  const handleBuildSave = async (e) => {
    e.preventDefault();

    if (!fileData) {
      showStatus('Choose an image to upload.');
      return;
    }

    if (!buildId || !buildname) {
      showStatus('Recipe info is missing. Close and try again.');
      return;
    }

    setBuildSaving(true);
    try {
      const data = new FormData();
      data.append('image', fileData);
      const uploadRes = await axiosPrivate.post('/single', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageFilename = uploadRes.data.filename || fileData.name;

      const buildPayload = {
        buildname,
        buildId,
        buildDescription: buildDescription.trim(),
        buildimagepath: imageFilename,
        buildimagepath2: '',
      };
      await addRecipieprops(buildPayload);
      await addImglink(buildPayload);
      await loadRecipes(activeBook);
      closeBuildModal();
      showStatus('Build image saved!');
    } catch (err) {
      console.error(err);
      showStatus(err.response?.data?.message || 'Could not save build image. Check the server and try again.');
    } finally {
      setBuildSaving(false);
    }
  };

  useEffect(() => () => {
    if (buildPreviewUrl) URL.revokeObjectURL(buildPreviewUrl);
  }, [buildPreviewUrl]);

  const handleCopyRecipe = async (recipe) => {
    try {
      const copyingOwnOriginal = isRecipeOwner(recipe) && !getRecipeLineage(recipe);
      const name = isRecipeOwner(recipe)
        ? `${recipe.recipiename} (copy)`
        : recipe.recipiename;
      const existing = getRecipeLineage(recipe);
      const source = copyingOwnOriginal
        ? null
        : {
            id: existing?.id || recipe.id,
            owner: existing?.owner || recipe.owner_username,
            name: existing?.name || recipe.recipiename,
          };
      await copyRecipe(recipe, { name, source });
      showStatus(`"${recipe.recipiename}" saved to My Recipes.`);
      setActiveBook('mine');
      await loadRecipes('mine');
    } catch (err) {
      showStatus(err.message || 'Could not copy this recipe.');
    }
  };

  const viewOriginal = async (recipe) => {
    const lineage = getRecipeLineage(recipe);
    if (!lineage?.id) return;
    setOriginalPreview({ pending: true, fallback: recipe, lineage });
    setHighlightId(lineage.id);
    if (activeBook !== 'community') {
      setActiveBook('community');
      await loadRecipes('community');
    }
  };

  const addRecipeToList = (recipe, multiplier = 1) => {
    const ingredients = getRecipeIngredients(recipe, multiplier);
    if (!ingredients.length) {
      showStatus('This recipe has no ingredients to add.');
      return;
    }
    addItems(ingredients, recipe.recipiename);
    showStatus(`Added ${ingredients.length} ingredient${ingredients.length === 1 ? '' : 's'} to your shopping list.`);
  };

  const copyRecipeText = async (recipe, multiplier = 1) => {
    try {
      await navigator.clipboard.writeText(recipeToClipboardText(recipe, multiplier));
      showStatus('Copied recipe to clipboard.');
    } catch {
      showStatus('Could not copy. Try selecting the ingredients instead.');
    }
  };

  const handleSaveRecipeEdits = async (recipe, payload) => {
    try {
      await updateRecipe(recipe.id, payload);
      showStatus('Recipe updated.');
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 405) {
        throw new Error('Ingredient edits need a server update. Name and notes can still be changed after deploy.');
      }
      throw err;
    }
  };

  function IngredientInputRow({ index }) {
    return (
      <div className="recipe-form-ingredient">
        <span className="recipe-form-ingredient-num">{index}</span>
        <input className="numinput" type="number" min="0" step="any" placeholder="Qty"
          onChange={(e) => setNumFns[index](e.target.value)} />
        <select defaultValue="Cups" onChange={(e) => setMeasFns[index](e.target.value)}>
          {MEASUREMENT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <input className="informationinput" type="text" placeholder="Ingredient name"
          onChange={(e) => setIngFns[index](e.target.value)} />
      </div>
    );
  }

  const meta = BOOK_META[activeBook];

  return (
    <div className="recipe-lab">
      <header className="recipe-lab-header">
        <Link to="/" className="recipe-lab-back">
          <i className="bi bi-arrow-left" /> Back to Lounge
        </Link>
        <h1 className="recipe-lab-title">Recipe Book</h1>
        <p className="recipe-lab-subtitle">{meta.description}</p>
      </header>

      {statusMessage && (
        <div className="recipe-status-banner" role="status">
          <i className="bi bi-info-circle-fill" /> {statusMessage}
        </div>
      )}

      <nav className="recipe-tabs" aria-label="Recipe collections">
        {Object.entries(BOOK_META).map(([key, book]) => (
          <button
            key={key}
            type="button"
            className={`recipe-tab${activeBook === key ? ' active' : ''}`}
            onClick={() => switchBook(key)}
          >
            <i className={`bi ${book.icon}`} />
            <span>{book.title}</span>
          </button>
        ))}
      </nav>

      <div className="recipe-toolbar">
        <div className="recipe-search-wrap">
          <i className="bi bi-search" />
          <input
            className="recipe-search"
            type="search"
            placeholder="Search by name, ingredient, or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {activeBook === 'mine' && (
          <button
            type="button"
            className="recipe-btn recipe-btn-primary"
            onClick={() => setShowAddRecipe((prev) => !prev)}
          >
            <i className={`bi ${showAddRecipe ? 'bi-x-lg' : 'bi-plus-lg'}`} />
            {showAddRecipe ? 'Cancel' : 'New Recipe'}
          </button>
        )}
        <Link to="/shopping-list" className="recipe-btn">
          <i className="bi bi-basket" /> List
        </Link>
        <span className="recipe-count">
          {isLoading ? 'Loading...' : `${filteredRecipes.length} recipe${filteredRecipes.length === 1 ? '' : 's'}`}
        </span>
      </div>

      {showAddRecipe && activeBook === 'mine' && (
        <section className="recipe-add-panel" key={formKey}>
          <h2 className="recipe-panel-title"><i className="bi bi-pencil-square" /> Create a new recipe</h2>
          <p className="recipe-panel-hint">Saved as private by default. You can publish it to the community later.</p>

          <label className="recipe-field-label" htmlFor="new-recipe-name">Recipe name</label>
          <input
            id="new-recipe-name"
            className="informationinput recipe-field"
            type="text"
            placeholder="e.g. Sunday Pancakes"
            onChange={(e) => setRecipiename(e.target.value)}
          />

          <h3 className="recipe-panel-subtitle">Ingredients</h3>
          {[1, 2, 3, 4, 5].map((n) => <IngredientInputRow key={n} index={n} />)}
          <Collapsible
            className="recipe-more-ingredients"
            trigger="Add more ingredients"
            triggerStyle={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
          >
            {[6, 7, 8, 9, 10, 11, 12].map((n) => <IngredientInputRow key={n} index={n} />)}
          </Collapsible>

          <label className="recipe-field-label" htmlFor="new-recipe-notes">Notes &amp; instructions</label>
          <textarea
            id="new-recipe-notes"
            className="notes recipe-field"
            placeholder="Tips, cook times, serving suggestions..."
            onChange={(e) => setNotes(e.target.value)}
          />

          <button
            type="button"
            className="recipe-btn recipe-btn-primary recipe-btn-wide"
            onClick={async () => {
              try {
                await addRecipie();
                resetRecipeForm();
                setFormKey((key) => key + 1);
                await loadRecipes('mine');
                setShowAddRecipe(false);
                showStatus('Recipe saved to My Recipes!');
              } catch (err) {
                showStatus(err.message || 'Could not save recipe.');
              }
            }}
          >
            <i className="bi bi-save" /> Save Recipe
          </button>
        </section>
      )}

      <Modal show={show} onHide={closeBuildModal} contentClassName="build-modal-content" centered>
        <Modal.Header closeButton className="build-modal-header">
          <Modal.Title>Add build image</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleBuildSave}>
          <Modal.Body className="build-modal-body">
            <p className="build-modal-recipe-name">{buildname}</p>
            <p className="build-modal-hint">
              Pick a photo for this recipe. Name and recipe link are filled in automatically.
            </p>

            <label className="build-modal-label" htmlFor="build-image-input">
              Build image
            </label>
            <input
              id="build-image-input"
              className="build-modal-file-input"
              type="file"
              accept="image/*"
              onChange={fileChangeHandler}
            />

            {buildPreviewUrl && (
              <img
                src={buildPreviewUrl}
                alt={`Preview for ${buildname}`}
                className="build-modal-preview"
              />
            )}

            <label className="build-modal-label" htmlFor="build-description-input">
              Caption <span className="build-modal-optional">(optional)</span>
            </label>
            <textarea
              id="build-description-input"
              className="build-modal-textarea"
              rows={3}
              placeholder="Short note about this build photo..."
              value={buildDescription}
              onChange={(e) => setBuildDescription(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer className="build-modal-footer">
            <Button type="button" variant="secondary" onClick={closeBuildModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={buildSaving || !fileData}>
              {buildSaving ? 'Saving...' : 'Save build image'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete recipe?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="danger"
            onClick={async () => {
              await deleteRecipe(deleteTarget.id);
              setDeleteTarget(null);
              showStatus('Recipe deleted.');
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!publishTarget} onHide={() => setPublishTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Publish to Community?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{publishTarget?.name}</strong> will be visible to everyone in the Community Book.
            You can still find it under My Recipes.
          </p>
          {publishTarget?.lineage && (
            <p>
              This is your version of{' '}
              <strong>{publishTarget.lineage.name || 'the original'}</strong>
              {publishTarget.lineage.owner ? <> by {publishTarget.lineage.owner}</> : null}.
              The original recipe and its creator stay credited.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPublishTarget(null)}>Cancel</Button>
          <Button variant="primary" onClick={handlePublishConfirm}>
            <i className="bi bi-globe" /> Publish Recipe
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!originalPreview && !originalPreview.pending && (originalPreview.missing || originalPreview.recipe)}
        onHide={() => { setOriginalPreview(null); setHighlightId(null); }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Original recipe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {originalPreview?.recipe && (
            <>
              <h3 className="recipe-card-title">{originalPreview.recipe.recipiename}</h3>
              {originalPreview.recipe.owner_username && (
                <p className="recipe-card-author">Created by {originalPreview.recipe.owner_username}</p>
              )}
              <ul className="original-preview-ingredients">
                {getRecipeIngredients(originalPreview.recipe).map((item) => (
                  <li key={item.slot}>{formatIngredientLine(item)}</li>
                ))}
              </ul>
              {originalPreview.recipe.notes ? (
                <p className="notes-text">{originalPreview.recipe.notes}</p>
              ) : null}
            </>
          )}
          {!originalPreview?.recipe && originalPreview?.missing && (
            <p>
              The original is no longer in the Community Book
              {originalPreview.lineage?.owner ? <> — it was created by <strong>{originalPreview.lineage.owner}</strong></> : null}
              {originalPreview.lineage?.name ? <> as <strong>{originalPreview.lineage.name}</strong></> : null}.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setOriginalPreview(null); setHighlightId(null); }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {isLoading ? (
        <div className="recipe-empty-state">
          <i className="bi bi-hourglass-split recipe-empty-icon" />
          <p>Loading recipes...</p>
        </div>
      ) : filteredRecipes.length === 0 ? (
        <div className="recipe-empty-state">
          <i className={`bi ${meta.icon} recipe-empty-icon`} />
          <p>{searchTerm ? `No recipes match “${searchTerm}”.` : meta.empty}</p>
          {activeBook === 'mine' && !searchTerm && (
            <button type="button" className="recipe-btn recipe-btn-primary" onClick={() => setShowAddRecipe(true)}>
              <i className="bi bi-plus-lg" /> Create your first recipe
            </button>
          )}
        </div>
      ) : (
        <div className="recipe-grid">
          {filteredRecipes.map((val) => {
            const multiplier = getMultiplier(val.id);
            const visibleIngredients = getRecipeIngredients(val, multiplier);
            const primaryIngredients = visibleIngredients.slice(0, 4);
            const extraIngredients = visibleIngredients.slice(4);
            const favorited = isFavorite(val.id);
            const isPrivate = val.visibility !== 'public';
            const showManage = canManage(val);
            const lineage = getRecipeLineage(val);

            return (
              <article
                key={val.id}
                id={`recipe-card-${val.id}`}
                className={`recipe-card${highlightId === val.id ? ' is-original-highlight' : ''}`}
              >
                <div className="recipe-card-top">
                  <div className="recipe-card-title-row">
                    <h3 className="recipe-card-title">{val.recipiename}</h3>
                    {activeBook === 'mine' && (
                      <span className={`recipe-badge ${isPrivate ? 'private' : 'public'}`}>
                        <i className={`bi ${isPrivate ? 'bi-lock' : 'bi-globe'}`} />
                        {isPrivate ? 'Private' : 'Published'}
                      </span>
                    )}
                  </div>
                  {val.owner_username && activeBook !== 'mine' && (
                    <p className="recipe-card-author">
                      {lineage ? `This version by ${val.owner_username}` : `by ${val.owner_username}`}
                    </p>
                  )}
                  {lineage && (
                    <p className="recipe-card-source">
                      Based on {lineage.name ? <strong>{lineage.name}</strong> : 'the original'}
                      {lineage.owner ? <> by {lineage.owner}</> : null}
                      {' '}
                      <button
                        type="button"
                        className="recipe-source-link"
                        onClick={() => viewOriginal(val)}
                      >
                        View original
                      </button>
                    </p>
                  )}
                </div>

                <div className="recipe-card-actions">
                  <button
                    type="button"
                    className={`recipe-icon-btn${favorited ? ' favorited' : ''}`}
                    onClick={() => toggleFavorite(val.id, activeBook === 'favorites')}
                    title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <i className={`bi ${favorited ? 'bi-heart-fill' : 'bi-heart'}`} />
                    {favorited ? 'Saved' : 'Favorite'}
                  </button>
                  <button
                    type="button"
                    className="recipe-icon-btn"
                    onClick={() => addRecipeToList(val, multiplier)}
                    title="Add ingredients to shopping list"
                  >
                    <i className="bi bi-basket" /> Add to list
                  </button>
                  <button
                    type="button"
                    className="recipe-icon-btn"
                    onClick={() => copyRecipeText(val, multiplier)}
                    title="Copy ingredients and notes"
                  >
                    <i className="bi bi-clipboard" /> Copy
                  </button>
                  {activeBook !== 'mine' && (
                    <button
                      type="button"
                      className="recipe-icon-btn"
                      onClick={() => handleCopyRecipe(val)}
                      title="Save a private copy to My Recipes"
                    >
                      <i className="bi bi-journal-plus" /> Save a copy
                    </button>
                  )}
                  {activeBook === 'mine' && (
                    <button
                      type="button"
                      className="recipe-icon-btn"
                      onClick={() => handleCopyRecipe(val)}
                      title="Duplicate this recipe"
                    >
                      <i className="bi bi-files" /> Duplicate
                    </button>
                  )}
                  {activeBook === 'mine' && isPrivate && (
                    <button
                      type="button"
                      className="recipe-icon-btn publish"
                      onClick={() => setPublishTarget({
                        id: val.id,
                        name: val.recipiename,
                        lineage: getRecipeLineage(val),
                      })}
                    >
                      <i className="bi bi-globe" /> Publish
                    </button>
                  )}
                  {val.imglink && (
                    <Nav.Link as={Link} to={`/recipes/${encodeURIComponent(val.imglink)}`} className="recipe-icon-btn link-btn">
                      <i className="bi bi-image" /> View build
                    </Nav.Link>
                  )}
                  {(!val.imglink || val.imglink === '') && canAddBuild(val) && (
                    <button type="button" className="recipe-icon-btn" onClick={() => openBuildModal(val)}>
                      <i className="bi bi-camera" /> Add build
                    </button>
                  )}
                </div>

                <div className="ingredients-section">
                  <div className="section-title-row">
                    <div className="section-title">Ingredients</div>
                    <div className="recipe-scale-bar" aria-label="Scale recipe">
                      {SCALE_PRESETS.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`recipe-scale-btn${multiplier === preset ? ' active' : ''}`}
                          onClick={() => setRecipeScale(val.id, preset)}
                        >
                          {preset === 0.5 ? '½' : `${preset}×`}
                        </button>
                      ))}
                    </div>
                  </div>
                  {primaryIngredients.map((item) => (
                    <IngredientRow
                      key={item.slot}
                      quantity={formatQty(item.quantity)}
                      measurement={item.measurement}
                      ingredientName={item.name}
                      isCompleted={!!completedIngredients[`${val.id}-${item.slot}`]}
                      onToggleDone={() => toggleDone(val.id, item.slot)}
                      onAddToList={() => addItems([item], val.recipiename)}
                    />
                  ))}
                  {extraIngredients.length > 0 && (
                    <Collapsible trigger="Show more ingredients" className="more-collapsible">
                      {extraIngredients.map((item) => (
                        <IngredientRow
                          key={item.slot}
                          quantity={formatQty(item.quantity)}
                          measurement={item.measurement}
                          ingredientName={item.name}
                          isCompleted={!!completedIngredients[`${val.id}-${item.slot}`]}
                          onToggleDone={() => toggleDone(val.id, item.slot)}
                          onAddToList={() => addItems([item], val.recipiename)}
                        />
                      ))}
                    </Collapsible>
                  )}
                  {visibleIngredients.length === 0 && (
                    <p className="recipe-no-ingredients">No ingredients listed.</p>
                  )}
                  {multiplier !== 1 && (
                    <p className="recipe-scale-note">Showing quantities at {multiplier}×</p>
                  )}
                </div>

                <div className="notes-section">
                  <Collapsible
                    className="notes-collapsible"
                    openedClassName="notes-collapsible is-open"
                    trigger={
                      <span className="notes-dropdown-trigger">
                        <i className="bi bi-journal-text" aria-hidden="true" />
                        <span>Notes{val.notes ? '' : ' (none)'}</span>
                        <i className="bi bi-chevron-down notes-chevron" aria-hidden="true" />
                      </span>
                    }
                  >
                    <p className="notes-text">{val.notes || 'No notes yet.'}</p>
                  </Collapsible>
                </div>

                {showManage && (
                  <Collapsible trigger="Edit recipe" className="manage-collapsible">
                    <RecipeManagePanel
                      recipe={val}
                      onSave={(payload) => handleSaveRecipeEdits(val, payload)}
                      onDelete={setDeleteTarget}
                    />
                  </Collapsible>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Recipes;
